<template>
  <div class="user-bookings-container">
    <!-- Loading Overlay -->
    <div v-if="showLoader" class="loading-overlay">
      <div class="loading-content">
        <div class="loading-spinner">
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
        </div>
        <p class="loading-text">{{ $t('incidents.loading') }}</p>
      </div>
    </div>

    <!-- Floating Action Buttons -->
    <div class="floating-actions">
      <transition name="fab-scale">
        <button 
          v-if="selectedReservations.length > 0" 
          class="fab delete-fab"
          @click="showDeleteBookingsDialog = true"
        >
          <v-icon size="24">mdi-trash-can</v-icon>
          <span class="fab-badge">{{ selectedReservations.length }}</span>
        </button>
      </transition>
      
      <button class="fab filter-fab" @click="toggleFilterDialog">
        <v-icon size="24">mdi-filter-variant</v-icon>
      </button>
    </div>

    <div class="bookings-content">
      <!-- Header Controls -->
      <div class="controls-section">
        <div class="date-range-selector glass-card">
          <div class="selector-header">
            <v-icon class="selector-icon">mdi-calendar-range</v-icon>
            <span class="selector-label">{{ $t('booking.date_range') }}</span>
          </div>
          
          <div class="selector-content">
            <v-select 
              v-model="selectedDateRange" 
              :items="dateOptions" 
              density="compact" 
              variant="solo"
              class="custom-select"
              @update:modelValue="onDateRangeChange"
              hide-details
            />
          </div>
        </div>

        <div class="stats-card glass-card">
          <div class="stat-item">
            <v-icon class="stat-icon">mdi-calendar-check</v-icon>
            <div class="stat-content">
              <div class="stat-number">{{ reservations?.length || 0 }}</div>
              <div class="stat-label">{{ $t('booking.bookings') }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="reservations?.length <= 0" class="empty-state">
        <div class="empty-card glass-card">
          <div class="empty-visual">
            <div class="empty-icon">
              <v-icon size="80">mdi-calendar-blank-outline</v-icon>
            </div>
            <div class="empty-decoration"></div>
          </div>
          
          <div class="empty-content">
            <h2 class="empty-title">{{ $t('booking.no-booking') }}</h2>
            <p class="empty-subtitle">{{ $t('booking.schedule-now') }}</p>
            
            <button class="create-booking-btn gradient-btn" @click="goToBookings()">
              <v-icon size="20">mdi-plus</v-icon>
              <span>{{ $t('booking.book-now-tab') }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Bookings List -->
      <div v-else class="bookings-list">
        <transition-group 
          name="booking-item" 
          tag="div" 
          class="bookings-grid"
          @enter="onBookingEnter"
          @leave="onBookingLeave"
        >
          <div 
            v-for="(reservation, index) in reservations" 
            :key="reservation.id"
            :data-index="index"
            class="booking-card-wrapper"
            @click="handeReservation(reservation)"
          >
            <div class="booking-card glass-card">
              <!-- Status Indicator -->
              <div class="status-indicator" :class="getStatusClass(reservation)"></div>
              
              <!-- Card Header -->
              <div class="booking-header">
                <div class="date-badges">
                  <div class="primary-badge">
                    {{ windowWidth < 440 ? moment(reservation.startTime).format('ddd') :
                      moment(reservation.startTime).format('dddd') }}
                  </div>
                  
                  <div v-if="reservation.recurringId === null" class="date-badge">
                    {{ windowWidth < 440 ? moment(reservation.startTime).format('DD.MM.YY') :
                      moment(reservation.startTime).format('DD.MM.YYYY') }}
                  </div>
                  
                  <div class="time-badge">
                    {{ moment(reservation.startTime).format('HH:mm') + ' - ' +
                      moment(reservation.endTime).format('HH:mm') }}
                  </div>
                </div>

                <!-- Recurring Indicator -->
                <div v-if="reservation.recurringId !== null" class="recurring-info">
                  <div class="recurring-badge">
                    <v-icon size="16">mdi-repeat</v-icon>
                    <span>{{ $t('messages.recurring-heading') }}</span>
                  </div>
                  
                  <div class="date-range">
                    <span class="range-start">{{ moment(reservation.startTime).format('DD.MM.YY') }}</span>
                    <v-icon size="16" class="range-arrow">mdi-chevron-right</v-icon>
                    <span class="range-end">{{ moment(reservation.recurrence.until).format('DD.MM.YY') }}</span>
                  </div>
                </div>
              </div>

              <!-- Card Content -->
              <div class="booking-content-area">
                <div class="booking-title">
                  <h3 class="title-text">
                    {{ reservation.title ? reservation.title : reservation.appointment.title }}
                  </h3>
                </div>

                <div class="booking-details">
                  <div class="detail-row">
                    <v-icon size="18" class="detail-icon">mdi-map-marker</v-icon>
                    <span class="detail-text">{{ reservation.poi.name }}</span>
                  </div>
                  
                  <div class="detail-row">
                    <v-icon size="18" class="detail-icon">mdi-stairs</v-icon>
                    <span class="detail-text">
                      {{ (reservation.floorName ? reservation.floorName + ", " : "") + $t('booking.floor') + ' #' + reservation.floor.number }}
                    </span>
                  </div>
                  
                  <div class="detail-row">
                    <v-icon size="18" class="detail-icon">mdi-tag</v-icon>
                    <span class="detail-text">{{ reservation.poiCategory }}</span>
                  </div>
                  
                  <div v-if="reservation.personCount" class="detail-row">
                    <v-icon size="18" class="detail-icon">mdi-account-group</v-icon>
                    <span class="detail-text">{{ reservation.personCount }} {{ $t('booking.people') }}</span>
                  </div>
                </div>

                <div class="booking-actions">
                  <button class="action-btn edit-btn" :disabled="!reservation?.appointment?.id">
                    <v-icon size="16">mdi-pencil</v-icon>
                    <span>{{ $t('booking.edit') }}</span>
                  </button>
                  
                  <button class="action-btn delete-btn" @click.stop="deleteConfirmation(reservation)">
                    <v-icon size="16">mdi-trash-can</v-icon>
                    <span>{{ $t('booking.delete') }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </transition-group>
      </div>
    </div>

    <!-- Filter Dialog -->
    <v-dialog v-model="filterDialogVisible" max-width="500" class="filter-dialog">
      <div class="dialog-content glass-card">
        <div class="dialog-header">
          <h2 class="dialog-title">{{ $t('booking.filter') }}</h2>
          <button class="close-btn" @click="filterDialogVisible = false">
            <v-icon>mdi-close</v-icon>
          </button>
        </div>

        <div class="dialog-body">
          <div class="filter-group">
            <label class="filter-label">{{ $t('booking.category') }}</label>
            <v-select 
              :items="categories" 
              item-value="name" 
              item-title="name" 
              v-model="selectedCategory"
              return-object
              class="custom-select"
            />
          </div>

          <div class="filter-group">
            <label class="filter-label">{{ $t('booking.floor') }}</label>
            <v-select 
              :items="floors" 
              item-value="name" 
              item-title="name" 
              v-model="selectedFloor"
              return-object
              class="custom-select"
            />
          </div>
        </div>

        <div class="dialog-actions">
          <button class="cancel-btn glass-btn" @click="cancelFilter">
            {{ $t('booking.cancel') }}
          </button>
          <button class="apply-btn gradient-btn" @click="applyFilters">
            {{ $t('booking.apply-filter') }}
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Custom Date Range Dialog -->
    <v-dialog v-model="dateRangeDialog" max-width="400" class="date-dialog">
      <div class="date-dialog-content">
        <div class="date-dialog-header">
          <h3>{{ $t('booking.custom-date-range') }}</h3>
        </div>
        
        <div class="date-picker-wrapper">
          <DatePicker 
            is-range 
            :min="new Date()" 
            :max="maxDate" 
            v-model.range="dates" 
            mode="date" 
            is-dark
            class="custom-date-picker"
          />
        </div>

        <div class="date-dialog-actions">
          <button class="cancel-btn glass-btn" @click="discardRange">
            {{ $t('booking.cancel') }}
          </button>
          <button class="confirm-btn gradient-btn" @click="confirmRange">
            {{ $t('booking.confirm') }}
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Booking Detail Modal -->
    <v-dialog v-model="showBookingDetail" max-width="600" class="booking-detail-dialog">
      <div class="booking-detail-card glass-card">
        <div class="detail-header">
          <div class="detail-actions">
            <button class="header-action-btn delete-action" @click="deleteConfirmation(selectedReservation)">
              <v-icon size="20">mdi-delete</v-icon>
              <span>{{ $t('booking.delete') }}</span>
            </button>
            
            <button 
              class="header-action-btn edit-action" 
              :disabled="!selectedReservation?.appointment?.id"
              @click="editBooking(selectedReservation)"
            >
              <v-icon size="20">mdi-pencil</v-icon>
              <span>{{ $t('booking.edit') }}</span>
            </button>
          </div>
          
          <button class="close-detail-btn" @click="showBookingDetail = false">
            <v-icon>mdi-close</v-icon>
          </button>
        </div>

        <div class="detail-content">
          <!-- Corruption Warning -->
          <div v-if="!selectedReservation?.appointment?.id" class="corruption-warning">
            <v-icon size="32" color="warning">mdi-alert-circle</v-icon>
            <div class="warning-content">
              <h4 class="warning-title">{{ $t('messages.title-the-booking-has-been-corrupted') }}</h4>
              <p class="warning-message">{{ $t('messages.text-please-delete-and-recreate-the-booking') }}</p>
            </div>
          </div>

          <!-- Booking Information -->
          <div class="booking-info-section">
            <h2 class="info-title">
              {{ selectedReservation?.title ?? selectedReservation?.appointment?.title }}
            </h2>

            <div class="info-grid">
              <div class="info-group">
                <h4 class="group-title">
                  <v-icon size="20">mdi-calendar-clock</v-icon>
                  <span>{{ $t('booking.date_range') }}</span>
                </h4>
                
                <div class="info-item">
                  <v-icon size="18">mdi-calendar</v-icon>
                  <span>{{ moment(selectedReservation?.startTime).format('dddd, DD.MM.YYYY') }}</span>
                </div>
                
                <div class="info-item">
                  <v-icon size="18">mdi-clock</v-icon>
                  <span>
                    {{ moment(selectedReservation?.startTime).format('HH:mm') }} - 
                    {{ moment(selectedReservation?.endTime).format('HH:mm') }}
                  </span>
                </div>
                
                <div class="info-item">
                  <v-icon size="18">mdi-account</v-icon>
                  <span>{{ selectedReservation?.creatorEmail }}</span>
                </div>
              </div>

              <div class="info-group">
                <h4 class="group-title">
                  <v-icon size="20">mdi-map-marker</v-icon>
                  <span>{{ $t('office.location') }}</span>
                </h4>
                
                <div class="info-item">
                  <v-icon size="18">mdi-door</v-icon>
                  <span>{{ selectedReservation?.poi?.name }}</span>
                </div>
                
                <div class="info-item">
                  <v-icon size="18">mdi-stairs</v-icon>
                  <span>
                    {{ (selectedReservation?.floorName ? selectedReservation?.floorName + ', ' : "") + $t('booking.floor') + ' #' + selectedReservation?.floor?.number }}
                  </span>
                </div>
                
                <div class="info-item">
                  <v-icon size="18">mdi-tag</v-icon>
                  <span>{{ selectedReservation?.poiCategory }}</span>
                </div>
              </div>

              <div class="info-group">
                <h4 class="group-title">
                  <v-icon size="20">mdi-information</v-icon>
                  <span>{{ $t('incidents.incident-details') }}</span>
                </h4>
                
                <div class="info-item">
                  <v-icon size="18">mdi-account-group</v-icon>
                  <span>{{ selectedReservation?.personCount || $t('booking.people') }} {{ $t('booking.people') }}</span>
                </div>
                
                <div v-if="selectedReservation?.recurringId" class="info-item">
                  <v-icon size="18">mdi-repeat</v-icon>
                  <span>{{ $t('booking.end') }} {{ moment(selectedReservation.recurrence.until).format('DD.MM.YYYY') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import lodash from 'lodash'
import { useRouter } from 'vue-router'
import moment from 'moment'
import { useOAuthService } from '@/services/OAuthService'

import {
  deleteAllAppointments,
  deleteAllReservations,
  getReservationsByCurrentUser,
  getAllAppointments,
  getUserProfile,
  getBuildings,
  deleteRecurrenceAppointment,
  deleteRecurrenceReservation,
  getPoiById,
  deleteAppointment,
  deleteReservation,
  getFloorById,
  getFloors,
  getPoiCategories,
  getAppointmentById,
  getReservationById,
  getPoiCategoryById,
  getReservationsByCurrentUserByTime,
  isPIA
} from '@/controllers/BaseController'
import Swal from 'sweetalert2'
import { DatePicker } from 'v-calendar'

export default {
  name: 'UserBookings',
  components: {
    DatePicker,
  },
  setup() {
    const { t } = useI18n()
    const OAuthService = useOAuthService()
    const router = useRouter()
    
    // Reactive state
    const showLoader = ref(true)
    const selectedReservations = ref([])
    const showDeleteBookingsDialog = ref(false)
    const dateRangeDialog = ref(false)
    const filterDialogVisible = ref(false)
    const showBookingDetail = ref(false)
    const selectedReservation = ref({})
    const windowWidth = ref(window.innerWidth)
    
    // Data
    const reservations = ref([])
    const currentUserProfile = ref({})
    const buildings = ref([])
    const categories = ref([])
    const floors = ref([])
    
    // Filters
    const selectedDateRange = ref('Current Month')
    const selectedCategory = ref({})
    const selectedFloor = ref({})
    const dates = ref({
      start: new Date(),
      end: null,
    })
    
    // Options - Now using translations
    const dateOptions = computed(() => [
      t('booking.all'),
      t('booking.today'),
      t('booking.tomorrow'),
      t('booking.current-week'),
      t('booking.next-week'),
      t('booking.current-month'),
      t('booking.next-month'),
      t('booking.custom')
    ])
    
    // Filter state
    const originalFilters = ref({
      selectedFloor: '',
      selectedCategory: '',
      dates: ['', ''],
      selectedDateRange: '',
    })
    const categoryChanged = ref(false)
    const floorChanged = ref(false)

    // Computed
    const maxDate = computed(() => {
      const today = new Date()
      return new Date(today.getFullYear(), today.getMonth(), today.getDate() + 56)
    })

    // Methods
    const getStatusClass = (reservation) => {
      const now = moment()
      const start = moment(reservation.startTime)
      const end = moment(reservation.endTime)
      
      if (now.isBefore(start)) return 'status-upcoming'
      if (now.isBetween(start, end)) return 'status-active'
      return 'status-past'
    }

    const onBookingEnter = (el) => {
      const index = parseInt(el.dataset.index)
      el.style.opacity = '0'
      el.style.transform = 'translateY(30px) scale(0.95)'
      
      setTimeout(() => {
        el.style.transition = 'all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1)'
        el.style.opacity = '1'
        el.style.transform = 'translateY(0) scale(1)'
      }, index * 100)
    }

    const onBookingLeave = (el) => {
      el.style.transition = 'all 0.3s ease-out'
      el.style.opacity = '0'
      el.style.transform = 'translateY(-20px) scale(0.9)'
    }

    const handleResize = () => {
      windowWidth.value = window.innerWidth
    }

    const handeReservation = (reservation) => {
      selectedReservation.value = { ...reservation }
      showBookingDetail.value = true
    }

    const toggleFilterDialog = () => {
      if (!filterDialogVisible.value) {
        originalFilters.value = {
          selectedFloor: selectedFloor.value,
          selectedCategory: selectedCategory.value,
        }
      }
      filterDialogVisible.value = !filterDialogVisible.value
    }

    const cancelFilter = () => {
      selectedFloor.value = originalFilters.value.selectedFloor
      selectedCategory.value = originalFilters.value.selectedCategory
      filterDialogVisible.value = false
    }

    const applyFilters = async () => {
      filterDialogVisible.value = false
      categoryChanged.value = true
      floorChanged.value = true
      await fetchPoisList()
    }

    const onDateRangeChange = (item) => {
      if (!item) return
      
      selectedDateRange.value = item
      
      // Use translated values for comparison
      const allText = t('booking.all')
      const todayText = t('booking.today')
      const tomorrowText = t('booking.tomorrow')
      const currentWeekText = t('booking.current-week')
      const nextWeekText = t('booking.next-week')
      const currentMonthText = t('booking.current-month')
      const nextMonthText = t('booking.next-month')
      const customText = t('booking.custom')
      
      if (item === allText) {
        dates.value = { start: null, end: null }
      } else if (item === todayText) {
        dates.value = {
          start: moment().toDate(),
          end: moment().endOf('day').toDate(),
        }
      } else if (item === tomorrowText) {
        dates.value = {
          start: moment().add(1, 'day').startOf('day').toDate(),
          end: moment().add(1, 'day').endOf('day').toDate(),
        }
      } else if (item === currentWeekText) {
        dates.value = {
          start: moment().toDate(),
          end: moment().endOf('week').toDate(),
        }
      } else if (item === nextWeekText) {
        dates.value = {
          start: moment().add(1, 'week').startOf('week').toDate(),
          end: moment().add(1, 'week').endOf('week').toDate(),
        }
      } else if (item === currentMonthText) {
        dates.value = {
          start: moment().toDate(),
          end: moment().endOf('month').toDate(),
        }
      } else if (item === nextMonthText) {
        dates.value = {
          start: moment().add(1, 'month').startOf('month').toDate(),
          end: moment().add(1, 'month').endOf('month').toDate(),
        }
      } else if (item === customText) {
        dates.value = {
          start: moment().toDate(),
          end: moment().add(7, 'days').toDate()
        }
        dateRangeDialog.value = true
        return
      }
      
      fetchPoisList()
    }

    const discardRange = () => {
      dateRangeDialog.value = false
    }

    const confirmRange = () => {
      dateRangeDialog.value = false
      fetchPoisList()
    }

    const goToBookings = () => {
      const bookingTab = document.getElementById('book-now-tab')
      if (bookingTab) {
        bookingTab.click()
      }
    }

    const deleteConfirmation = (reservation) => {
      showBookingDetail.value = false
      
      Swal.fire({
        title: t('booking.delete'),
        text: t('messages.text-revert'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#f44336',
        cancelButtonColor: '#6c757d',
        confirmButtonText: t('booking.delete'),
        cancelButtonText: t('booking.cancel'),
        customClass: {
          popup: 'swal-custom'
        }
      }).then(result => {
        if (result.isConfirmed) {
          if (reservation?.recurringId) {
            deleteRecurringBooking(reservation)
          } else {
            deleteBooking(reservation)
          }
        } else {
          showBookingDetail.value = true
        }
      })
    }

    const deleteBooking = async (reservation) => {
      showLoader.value = true
      
      try {
        if (reservation?.appointment?.id) {
          await deleteAppointment(reservation.appointment.id)
        }
        await deleteReservation(reservation.id)
        
        Swal.fire({
          icon: 'success',
          title: t('incidents.deleted-incident'),
          text: t('messages.booking-deletion-successful'),
          timer: 2000,
          showConfirmButton: false
        })
        
        await fetchPoisList()
      } catch (error) {
        console.error('Delete error:', error)
        Swal.fire({
          icon: 'error',
          title: t('messages.error-incident'),
          text: t('messages.booking-deletion-failed-text')
        })
      } finally {
        showLoader.value = false
      }
    }

    const deleteRecurringBooking = async (reservation) => {
      showLoader.value = true
      
      try {
        if (reservation?.appointment?.recurringId) {
          await deleteRecurrenceAppointment(reservation.appointment.recurringId)
        }
        await deleteRecurrenceReservation(reservation.recurringId)
        
        Swal.fire({
          icon: 'success',
          title: t('incidents.deleted-incident'),
          text: t('messages.booking-deletion-successful'),
          timer: 2000,
          showConfirmButton: false
        })
        
        await fetchPoisList()
      } catch (error) {
        console.error('Delete recurring error:', error)
        Swal.fire({
          icon: 'error',
          title: t('messages.error-incident'),
          text: t('messages.booking-deletion-failed-text')
        })
      } finally {
        showLoader.value = false
      }
    }

    const editBooking = async (booking) => {
      if (booking?.recurringId) {
        Swal.fire({
          icon: 'info',
          title: t('messages.recurring-heading'),
          text: t('messages.recurring-text'),
        })
        return
      }

      try {
        showLoader.value = true
        showBookingDetail.value = false
        
        const category = await getPoiCategoryById(booking?.poi?.poiCategories?.ids[0])
        
        await router.push({
          name: 'editManageBooking',
          params: {
            building_id: booking.poi.buildingId,
            category_id: category?.id,
            poi_id: booking?.poi?.id,
            reservation_id: booking?.id,
            appointment_id: booking?.appointment?.id,
          },
        })
      } catch (error) {
        console.error('Navigation error:', error)
      } finally {
        showLoader.value = false
      }
    }

    const fetchPoisList = async () => {
      try {
        showLoader.value = true
        
        const userProfile = currentUserProfile.value
        let startDate = dates.value?.start
        let endDate = dates.value?.end
        
        if (selectedDateRange.value === t('booking.all')) {
          startDate = new Date()
          endDate = moment().add(60, 'day').endOf('day').toDate()
        }

        const poiReservations = await getReservationsByCurrentUserByTime(startDate, endDate)
        
        // Process reservations
        const futureRecurringIds = new Set()
        poiReservations.forEach(reservation => {
          if (reservation.recurringId && moment(reservation.endTime).isAfter(moment())) {
            futureRecurringIds.add(reservation.recurringId)
          }
        })

        let filteredReservations = poiReservations.filter(reservation => 
          (reservation.recurringId && futureRecurringIds.has(reservation.recurringId)) ||
          (!reservation.recurringId && moment(reservation?.endTime).isSameOrAfter(moment()) && reservation?.creatorEmail === userProfile.email)
        )

        const reservationMap = {}

        for (const reservation of filteredReservations) {
          if (!reservation?.poiId) {
            const fullReservation = await getReservationById(reservation?.id)
            Object.assign(reservation, fullReservation)
          }

          const poi = await getPoiById(reservation?.poiId)
          reservation.poi = poi

          const floor = await getFloorById(poi?.floors?.ids[0])
          reservation.floor = floor

          if (reservation.recurringId) {
            if (!reservationMap[reservation.recurringId]) {
              reservationMap[reservation.recurringId] = {
                ...reservation,
                count: [],
              }
            } else {
              if (moment(reservation.startTime).isBefore(moment(reservationMap[reservation.recurringId].startTime))) {
                const currentCount = reservationMap[reservation.recurringId].count
                currentCount.push(reservationMap[reservation.recurringId])
                reservationMap[reservation.recurringId] = {
                  ...reservation,
                  count: currentCount,
                }
              }
            }
            reservationMap[reservation.recurringId].count.push(reservation)
            reservationMap[reservation.recurringId].count.sort((a, b) => 
              moment(a.startTime).isBefore(moment(b.startTime)) ? -1 : 1
            )
          } else {
            reservationMap[reservation.id] = reservation
          }
        }

        // Add dummy data for PIA users
        const isPIAUser = await isPIA()
        if (isPIAUser) {
          const dummyReservations = [
            {
              id: `dummy-${Date.now()}-1`,
              startTime: moment().startOf('month').add(2, 'days').hour(9).minute(0).toDate(),
              endTime: moment().startOf('month').add(2, 'days').hour(17).minute(0).toDate(),
              title: 'Team Strategy Meeting',
              poi: {
                id: '875d660d-7d76-4b47-9117-c057c023e274',
                name: '3.02b Cloud Conference Room',
                buildingId: 'e1e1c326-f8e5-472b-9ea7-673c59550d46',
                floors: { ids: ['365a50d0-a10a-4586-b79d-3a22577f85ca'] },
                poiCategories: { ids: ['bfcaea7c-17a5-4434-a729-dbcbccdc58ba'] },
              },
              floor: {
                id: '365a50d0-a10a-4586-b79d-3a22577f85ca',
                name: '3rd Floor',
                number: 3,
              },
              poiCategory: 'Meeting Room',
              creatorEmail: userProfile.email,
              appointment: {
                id: `dummy-appointment-${Date.now()}-1`,
                title: 'Team Strategy Meeting',
              },
              personCount: 8,
              recurringId: null,
            },
            {
              id: `dummy-${Date.now()}-2`,
              startTime: moment().startOf('month').add(5, 'days').hour(14).minute(0).toDate(),
              endTime: moment().startOf('month').add(5, 'days').hour(16).minute(0).toDate(),
              title: 'Product Review Session',
              poi: {
                id: '875d660d-7d76-4b47-9117c057c023e274',
                name: '3.02b Cloud Conference Room',
                buildingId: 'e1e1c326-f8e5-472b-9ea7-673c59550d46',
                floors: { ids: ['365a50d0-a10a-4586-b79d-3a22577f85ca'] },
                poiCategories: { ids: ['bfcaea7c-17a5-4434-a729-dbcbccdc58ba'] },
              },
              floor: {
                id: '365a50d0-a10a-4586-b79d-3a22577f85ca',
                name: '3rd Floor',  
                number: 3,
              },
              poiCategory: 'Meeting Room',
              creatorEmail: userProfile.email,
              appointment: {
                id: `dummy-appointment-${Date.now()}-2`,
                title: 'Product Review Session',
              },
              personCount: 5,
              recurringId: null,
            },
          ]

          dummyReservations.forEach(dummy => {
            reservationMap[dummy.id] = dummy
          })
        }

        let finalReservations = lodash.orderBy(
          Object.values(reservationMap), 
          [reservation => moment(reservation?.startTime)], 
          ['asc']
        )

        // Apply filters
        if (floorChanged.value && selectedFloor.value?.id !== 1) {
          finalReservations = finalReservations.filter(reservation => 
            reservation.floorName === selectedFloor.value?.name
          )
        }
        
        if (categoryChanged.value && selectedCategory.value.id !== 1) {
          finalReservations = finalReservations.filter(reservation => 
            reservation.poiCategory === selectedCategory.value?.name
          )
        }

        reservations.value = finalReservations
        
      } catch (error) {
        console.error('Fetch error:', error)
        Swal.fire({
          icon: 'error',
          title: t('messages.error-incident'),
          text: t('incidents.loading-error-description')
        })
      } finally {
        showLoader.value = false
      }
    }

    // Lifecycle
    onMounted(async () => {
      try {
        showLoader.value = true
        window.addEventListener('resize', handleResize)

        const [userProfile, buildingsData, poiCategoriesData, poiFloorsData] = await Promise.all([
          OAuthService.getUserProfile(),
          getBuildings(),
          getPoiCategories(),
          getFloors(),
        ])

        currentUserProfile.value = userProfile
        buildings.value = buildingsData

        const poiCategories = lodash.filter(
          lodash.orderBy(poiCategoriesData, 'name', 'asc'),
          category => category.active && !category.name.includes('Room Control')
        )
        poiCategories.unshift({ id: 1, name: t('booking.all') })

        const poiFloors = lodash.filter(
          lodash.orderBy(poiFloorsData, 'name', 'asc'), 
          floor => floor.active
        )
        poiFloors.unshift({ id: 1, name: t('booking.all') })

        categories.value = poiCategories
        floors.value = poiFloors
        selectedFloor.value = poiFloors[0]
        selectedCategory.value = poiCategories[0]

        await onDateRangeChange(t('booking.current-month'))
      } catch (error) {
        console.error('Initialization error:', error)
      } finally {
        showLoader.value = false
      }
    })

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize)
    })

    return {
      // State
      showLoader,
      selectedReservations,
      showDeleteBookingsDialog,
      dateRangeDialog,
      filterDialogVisible,
      showBookingDetail,
      selectedReservation,
      windowWidth,
      
      // Data
      reservations,
      currentUserProfile,
      buildings,
      categories,
      floors,
      
      // Filters
      selectedDateRange,
      selectedCategory,
      selectedFloor,
      dates,
      dateOptions,
      
      // Computed
      maxDate,
      
      // Methods
      getStatusClass,
      onBookingEnter,
      onBookingLeave,
      handeReservation,
      toggleFilterDialog,
      cancelFilter,
      applyFilters,
      onDateRangeChange,
      discardRange,
      confirmRange,
      goToBookings,
      deleteConfirmation,
      deleteBooking,
      deleteRecurringBooking,
      editBooking,
      fetchPoisList,   
      moment,
      router,
      t,
    }
  },
}
</script>

<style scoped>
.user-bookings-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fbff 0%, #e3f2fd 100%);
  position: relative;
}

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(15px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-content {
  text-align: center;
}

.loading-spinner {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-top: 3px solid #4fc3f7;
  border-radius: 50%;
  animation: spin 1.2s ease-in-out infinite;
}

.spinner-ring:nth-child(1) { animation-delay: 0s; }
.spinner-ring:nth-child(2) { animation-delay: 0.15s; opacity: 0.8; }
.spinner-ring:nth-child(3) { animation-delay: 0.3s; opacity: 0.6; }

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  color: #2196f3;
  font-size: 16px;
  font-weight: 500;
  margin: 0;
}

/* Floating Action Buttons */
.floating-actions {
  position: fixed;
  right: 24px;
  bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  z-index: 1000;
}

.fab {
  position: relative;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fab:hover {
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.2);
}

.delete-fab {
  background: linear-gradient(135deg, #ff5252 0%, #f44336 100%);
  color: white;
}

.filter-fab {
  background: linear-gradient(135deg, #4fc3f7 0%, #2196f3 100%);
  color: white;
}

.fab-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #ff1744;
  color: white;
  font-size: 10px;
  font-weight: bold;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fab-scale-enter-active,
.fab-scale-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fab-scale-enter-from,
.fab-scale-leave-to {
  opacity: 0;
  transform: scale(0);
}

/* Enhanced Glass Card Base */
.glass-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 16px;
  box-shadow: 
    0 4px 20px rgba(31, 38, 135, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.glass-card:hover {
  transform: translateY(-1px);
  box-shadow: 
    0 6px 25px rgba(31, 38, 135, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

/* Main Content */
.bookings-content {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}

/* Improved Controls Section */
.controls-section {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 16px;
  margin-bottom: 32px;
}

/* Improved Date Range Selector */
.date-range-selector {
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 70px;
}

.selector-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
  flex-shrink: 0;
}

.selector-icon {
  color: #2196f3;
  font-size: 20px;
}

.selector-label {
  font-size: 14px;
  font-weight: 600;
  color: #1976d2;
  white-space: nowrap;
}

.selector-content {
  flex: 1;
}

.custom-select {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 10px;
  min-height: 40px;
}

.custom-select :deep(.v-field) {
  border-radius: 10px;
}

.custom-select :deep(.v-field__input) {
  min-height: 40px;
  font-size: 14px;
  font-weight: 500;
  color: #1976d2;
}

/* Improved Stats Card */
.stats-card {
  padding: 16px 20px;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 70px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-start;
  width: 100%;
}

.stat-icon {
  color: #4caf50;
  font-size: 24px;
  flex-shrink: 0;
}

.stat-content {
  text-align: left;
  flex: 1;
}

.stat-number {
  font-size: 20px;
  font-weight: 700;
  color: #1976d2;
  line-height: 1;
  margin-bottom: 2px;
}

.stat-label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Empty State */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 500px;
}

.empty-card {
  text-align: center;
  padding: 60px 40px;
  max-width: 500px;
  position: relative;
  overflow: hidden;
}

.empty-visual {
  position: relative;
  margin-bottom: 40px;
}

.empty-icon {
  color: rgba(33, 150, 243, 0.3);
  margin-bottom: 20px;
}

.empty-decoration {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, rgba(79, 195, 247, 0.1) 0%, rgba(33, 150, 243, 0.1) 100%);
  border-radius: 50%;
  z-index: -1;
}

.empty-title {
  font-size: 28px;
  font-weight: 700;
  color: #1976d2;
  margin: 0 0 16px 0;
}

.empty-subtitle {
  color: #666;
  font-size: 16px;
  line-height: 1.5;
  margin: 0 0 32px 0;
}

.create-booking-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  background: linear-gradient(135deg, #4fc3f7 0%, #2196f3 100%);
  color: white;
  border: none;
  border-radius: 16px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin: 0 auto;
}

.create-booking-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(33, 150, 243, 0.4);
}

/* Bookings List */
.bookings-list {
  min-height: 400px;
}

.bookings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 24px;
}

.booking-card-wrapper {
  cursor: pointer;
}

.booking-card {
  position: relative;
  padding: 0;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 20px;
}

.booking-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 48px rgba(31, 38, 135, 0.2);
}

.status-indicator {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
}

.status-upcoming {
  background: linear-gradient(135deg, #4caf50 0%, #66bb6a 100%);
}

.status-active {
  background: linear-gradient(135deg, #ff9800 0%, #ffa726 100%);
}

.status-past {
  background: linear-gradient(135deg, #9e9e9e 0%, #bdbdbd 100%);
}

.booking-header {
  padding: 20px 24px 16px;
  background: linear-gradient(135deg, rgba(79, 195, 247, 0.05) 0%, rgba(33, 150, 243, 0.05) 100%);
  border-bottom: 1px solid rgba(79, 195, 247, 0.2);
}

.date-badges {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.primary-badge {
  background: linear-gradient(135deg, #4fc3f7 0%, #2196f3 100%);
  color: white;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.date-badge {
  background: rgba(33, 150, 243, 0.1);
  color: #1976d2;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
}

.time-badge {
  background: rgba(76, 175, 80, 0.1);
  color: #388e3c;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
}

.recurring-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
}

.recurring-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(156, 39, 176, 0.1);
  color: #7b1fa2;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
}

.date-range {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #666;
}

.range-arrow {
  color: #999;
}

.booking-content-area {
  padding: 20px 24px;
}

.booking-title {
  margin-bottom: 16px;
}

.title-text {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.3;
}

.booking-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #666;
}

.detail-icon {
  color: #4fc3f7;
  flex-shrink: 0;
}

.detail-text {
  line-height: 1.4;
}

.booking-actions {
  display: flex;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid rgba(79, 195, 247, 0.2);
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;
}

.edit-btn {
  color: #2196f3;
  border-color: rgba(33, 150, 243, 0.3);
}

.edit-btn:hover:not(:disabled) {
  background: rgba(33, 150, 243, 0.1);
}

.edit-btn:disabled {
  color: #ccc;
  border-color: #eee;
  cursor: not-allowed;
}

.delete-btn {
  color: #f44336;
  border-color: rgba(244, 67, 54, 0.3);
}

.delete-btn:hover {
  background: rgba(244, 67, 54, 0.1);
}

/* Dialog Styles */
.filter-dialog :deep(.v-overlay__content),
.date-dialog :deep(.v-overlay__content),
.booking-detail-dialog :deep(.v-overlay__content) {
  margin: 0;
}

.dialog-content,
.date-dialog-content,
.booking-detail-card {
  width: 100%;
  margin: 20px;
  max-height: calc(100vh - 40px);
  overflow: auto;
}

.dialog-header,
.date-dialog-header,
.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 20px;
  border-bottom: 1px solid rgba(79, 195, 247, 0.2);
}

.date-dialog-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.dialog-title {
  font-size: 20px;
  font-weight: 600;
  color: #1976d2;
  margin: 0;
}

.close-btn,
.close-detail-btn {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 4px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.close-btn:hover,
.close-detail-btn:hover {
  background: rgba(0, 0, 0, 0.1);
}

.dialog-body {
  padding: 24px;
}

.filter-group {
  margin-bottom: 24px;
}

.filter-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.dialog-actions,
.date-dialog-actions {
  display: flex;
  gap: 12px;
  padding: 20px 24px 24px;
  border-top: 1px solid rgba(79, 195, 247, 0.2);
}

.date-dialog-actions {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.glass-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(33, 150, 243, 0.3);
  border-radius: 12px;
  color: #1976d2;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.glass-btn:hover {
  background: rgba(33, 150, 243, 0.1);
  transform: translateY(-1px);
}

.gradient-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  background: linear-gradient(135deg, #4fc3f7 0%, #2196f3 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.gradient-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(33, 150, 243, 0.3);
}

.gradient-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* Date Picker */
.date-picker-wrapper {
  padding: 20px;
  display: flex;
  justify-content: center;
}

.custom-date-picker {
  border-radius: 16px;
}

/* Booking Detail Dialog */
.booking-detail-card {
  max-width: 600px;
}

.detail-actions {
  display: flex;
  gap: 12px;
}

.header-action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;
}

.delete-action {
  color: #f44336;
  border-color: rgba(244, 67, 54, 0.3);
}

.delete-action:hover {
  background: rgba(244, 67, 54, 0.1);
}

.edit-action {
  color: #2196f3;
  border-color: rgba(33, 150, 243, 0.3);
}

.edit-action:hover:not(:disabled) {
  background: rgba(33, 150, 243, 0.1);
}

.edit-action:disabled {
  color: #ccc;
  border-color: #eee;
  cursor: not-allowed;
}

.detail-content {
  padding: 24px;
}

.corruption-warning {
  display: flex;
  align-items: center;
  gap: 16px;
  background: rgba(255, 152, 0, 0.1);
  border: 1px solid rgba(255, 152, 0, 0.3);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
}

.warning-content h4 {
  margin: 0 0 4px 0;
  color: #f57c00;
  font-size: 16px;
}

.warning-content p {
  margin: 0;
  color: #ef6c00;
  font-size: 14px;
}

.booking-info-section {
  max-width: none;
}

.info-title {
  font-size: 24px;
  font-weight: 600;
  color: #1976d2;
  margin: 0 0 24px 0;
  text-align: center;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
}

.info-group {
  background: rgba(79, 195, 247, 0.05);
  border: 1px solid rgba(79, 195, 247, 0.2);
  border-radius: 12px;
  padding: 20px;
}

.group-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px 0;
  color: #1976d2;
  font-size: 16px;
  font-weight: 600;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  font-size: 14px;
  color: #666;
}

.info-item:last-child {
  margin-bottom: 0;
}

/* Booking Item Animations */
.booking-item-enter-active,
.booking-item-leave-active {
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.booking-item-enter-from {
  opacity: 0;
  transform: scale(0.9) translateY(20px);
}

.booking-item-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(-20px);
}

/* Custom SweetAlert2 */
:global(.swal-custom) {
  border-radius: 20px !important;
  font-family: inherit !important;
}

/* Responsive Design for Cards */
@media (max-width: 1200px) {
  .bookings-content {
    padding: 20px;
  }
  
  .bookings-grid {
    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  }
}

@media (max-width: 968px) {
  .bookings-content {
    padding: 16px;
  }
  
  .controls-section {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .date-range-selector,
  .stats-card {
    min-height: 60px;
    padding: 12px 16px;
  }
  
  .selector-header {
    gap: 6px;
  }
  
  .selector-label {
    font-size: 13px;
  }
  
  .stat-number {
    font-size: 18px;
  }
  
  .stat-label {
    font-size: 11px;
  }
  
  .bookings-grid {
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 20px;
  }
  
  .floating-actions {
    right: 16px;
    bottom: 16px;
  }
}

@media (max-width: 768px) {
  .bookings-content {
    padding: 12px;
  }
  
  .date-range-selector {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    min-height: auto;
    padding: 12px 16px;
  }
  
  .selector-content {
    width: 100%;
  }
  
  .stats-card {
    justify-content: center;
  }
  
  .stat-item {
    justify-content: center;
  }
  
  .bookings-grid {
    grid-template-columns: 1fr;
  }
  
  .booking-card {
    margin: 0;
  }
  
  .dialog-content,
  .date-dialog-content,
  .booking-detail-card {
    margin: 10px;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 450px) {
  .bookings-content {
    padding: 8px;
  }
  
  .controls-section {
    gap: 8px;
  }
  
  .date-range-selector,
  .stats-card {
    padding: 10px 14px;
  }
  
  .selector-icon,
  .stat-icon {
    font-size: 18px;
  }
  
  .stat-number {
    font-size: 16px;
  }
  
  .booking-header {
    padding: 16px 20px 12px;
  }
  
  .booking-content-area {
    padding: 16px 20px;
  }
  
  .title-text {
    font-size: 16px;
  }
  
  .date-badges {
    gap: 6px;
  }
  
  .primary-badge,
  .date-badge,
  .time-badge {
    padding: 4px 8px;
    font-size: 11px;
  }
  
  .floating-actions {
    right: 12px;
    bottom: 12px;
  }
  
  .fab {
    width: 48px;
    height: 48px;
  }
}

@media (max-width: 355px) {
  .bookings-content {
    padding: 4px;
  }
  
  .booking-header {
    padding: 12px 16px 10px;
  }
  
  .booking-content-area {
    padding: 12px 16px;
  }
  
  .selector-header,
  .group-title {
    font-size: 14px;
  }
  
  .title-text {
    font-size: 15px;
  }
  
  .dialog-content,
  .date-dialog-content,
  .booking-detail-card {
    margin: 5px;
  }
  
  .dialog-header,
  .date-dialog-header,
  .detail-header {
    padding: 16px 16px 12px;
  }
  
  .dialog-body,
  .detail-content {
    padding: 16px;
  }
  
  .dialog-actions,
  .date-dialog-actions {
    padding: 12px 16px 16px;
  }
  
  .info-title {
    font-size: 20px;
  }
  
  .empty-card {
    padding: 40px 20px;
  }
  
  .empty-title {
    font-size: 24px;
  }
  
  .empty-subtitle {
    font-size: 14px;
  }
}

.date-dialog-content {
  position: relative;
  z-index: 1;
  max-width: 90vw;
  max-height: 90vh;
  overflow: auto;
  margin: 16px;
  background: #0F162A;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  border: none;
}

.date-dialog-header h3 {
  margin: 0 0 6px 0;
  color: #1976d2;
  font-size: 16px;
  font-weight: 600;
}
</style>
<!-- UserBookings Component

This component provides a comprehensive interface for users to manage their bookings. It includes functionalities for viewing, filtering, and managing reservations.

At the top of the component, there is a delete button that appears if the user has selected any reservations. This button allows users to delete multiple bookings at once.

A Loader component is used to indicate loading states, ensuring users are aware when data is being fetched or processed.

The date range selector allows users to filter bookings based on predefined date ranges such as Today, Tomorrow, Current Week, Next Week, Current Month, Next Month, or a Custom range. Users can select a date range from a dropdown menu, and if 'Custom' is selected, a date picker dialog appears for users to specify the start and end dates.

A filter button next to the date range selector opens a dialog where users can filter bookings by category and floor. This helps users narrow down their search to specific types of bookings or locations.

If there are no bookings, a card is displayed with a message indicating no bookings are available. This card includes an image and a button that redirects users to the booking creation page.

Each booking is displayed as a card with details such as the date, time, and location. Clicking on a booking card opens a detailed view of the booking in a dialog. This detailed view includes options to delete or edit the booking. If the booking is corrupted, a message is displayed advising the user to delete and recreate the booking.

The component uses Swal for displaying alerts. For example, when a booking is successfully deleted, a success alert is shown. If there is an error during deletion, an error alert is displayed. These alerts provide immediate feedback to users about the actions they have performed.

Overall, this component enhances the user experience by providing a clear and interactive interface for managing bookings, with visual feedback and easy-to-use filtering options. -->